<template>
  <main role="main">
    <div class="container py-4" style="max-width: 700px;">
      <h4 class="mb-4">{{ $t("configPasswordPolicy") }}</h4>
      <div v-if="isLoading" class="py-5 text-center"><b-spinner/></div>
      <b-form v-if="!isLoading && currentPasswordPolicy" @keydown="formKeyDown" @submit.prevent="submitForm">
        <b-form-row>
          <b-form-group class="col-lg-6" label-for="input-min-password-length">
            <template v-slot:label>{{ $t("passwordMinLength") }} <span class="text-danger">*</span></template>
            <b-form-input id="input-min-password-length" type="number" required min="6" max="16"
                          v-model="minPasswordLength" name="minPasswordLength"/>
          </b-form-group>
          <b-form-group class="col-lg-6" label-for="input-max-password-length">
            <template v-slot:label>{{ $t("passwordMaxLength") }} <span class="text-danger">*</span></template>
            <b-form-input id="input-max-password-length" type="number" required min="6" max="32"
                          v-model="maxPasswordLength" name="maxPasswordLength"></b-form-input>
          </b-form-group>
        </b-form-row>
        <b-form-group label-for="input-password-pattern" :label="$t('passwordPattern')">
          <b-form-input id="input-password-pattern" type="text" v-model="passwordPattern" name="passwordPattern" maxlength="255"/>
        </b-form-group>
        <b-form-group label-for="input-password-description" :label="$t('passwordDescription')">
          <b-form-input id="input-password-description" type="text" maxlength="255"
                        v-model="passwordDescription" name="passwordDescription"></b-form-input>
        </b-form-group>
        <b-form-group label-for="input-password-validation-failed-message" :label="$t('passwordValidationFailedMessage')">
          <b-form-input id="input-password-validation-failed-message" type="text" maxlength="255"
                        v-model="passwordValidationFailedMessage" name="passwordValidationFailedMessage" ></b-form-input>
        </b-form-group>
        <b-form-row>
          <b-form-group class="col-lg-6" label-for="input-password-validity-period">
            <template v-slot:label>{{ $t("passwordValidityPeriod") }} <span class="text-danger">*</span></template>
            <b-form-input id="input-password-validity-period" type="number" required min="1" max="3650"
                          v-model="passwordValidityPeriod" name="passwordValidityPeriod"></b-form-input>
          </b-form-group>
          <b-form-group class="col-lg-6" label-for="input-wrong-password-attempt">
            <template v-slot:label>{{ $t("wrongPasswordAttempt") }} <span class="text-danger">*</span></template>
            <b-form-input id="input-wrong-password-attempt" type="number" required min="1" max="100"
                          v-model="wrongPasswordAttempt" name="wrongPasswordAttempt"></b-form-input>
          </b-form-group>
        </b-form-row>
        <b-form-row>
          <b-form-group class="col-lg-6" label-for="radio-locked-out" :label="$t('passwordLockedOut')">
            <b-form-radio-group id="radio-locked-out" :options="lockedOutOptions"
                                v-model="lockedOut" name="lockedOut"></b-form-radio-group>
          </b-form-group>
          <b-form-group class="col-lg-6" label-for="input-locked-out-duration">
            <template v-slot:label>{{ $t("passwordLockedOutDuration") }} <span class="text-danger">*</span></template>
            <b-form-input id="input-locked-out-duration" type="number" required min="1" max="1440"
                          v-model="lockedOutDuration" name="lockedOutDuration"></b-form-input>
          </b-form-group>
        </b-form-row>
        <b-form-group label-for="radio-must-change-default-password" :label="$t('mustChangeDefaultPassword')">
          <b-form-radio-group id="radio-must-change-default-password" :options="mustChangeDefaultPasswordOptions"
                              v-model="mustChangeDefaultPassword" name="mustChangeDefaultPassword"></b-form-radio-group>
        </b-form-group>

        <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>

        <div class="text-center pt-3">
          <b-button type="submit" variant="primary" style="width: 120px;"
                    :disabled="isSubmitting" @click="beforeSubmit">{{ $t("save") }}</b-button>
        </div>
      </b-form>
    </div>
  </main>

</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Emit, Prop} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as passwordPolicyService from "@/service/passwordPolicyService";

  @Component
  export default class PasswordPolicy extends BaseComponent {

    isLoading = true;

    currentPasswordPolicy = null;

    minPasswordLength = 0;
    maxPasswordLength = 0;
    passwordPattern = "";
    passwordDescription = "";
    passwordValidationFailedMessage = "";
    passwordValidityPeriod = 0;
    wrongPasswordAttempt = 0;

    lockedOutOptions = [
      { value: true, text: this.$t("yes").toString() },
      { value: false, text: this.$t("no").toString() },
    ];
    lockedOut = true;

    lockedOutDuration = 0;

    mustChangeDefaultPasswordOptions = [
      { value: true, text: this.$t("yes").toString() },
      { value: false, text: this.$t("no").toString() },
    ];
    mustChangeDefaultPassword = true;

    formErrorMsg = "";
    isSubmitting = false;

    // Emit decorators only
    @Emit("submitted") __submitted() {}


    mounted() {
      this.fetchPasswordPolicy();
    }

    async fetchPasswordPolicy() {
      try {        
        this.isLoading = true;
        this.currentPasswordPolicy = await passwordPolicyService.get();
        Object.assign(this, this.currentPasswordPolicy);

      } catch (e) {
        console.error(e);

      } finally {
        this.isLoading = false;
      }
    }

    formKeyDown(event) {
      return event.key !== 'Enter';
    }

    beforeSubmit() {
      const trimFields = ["passwordPattern", "passwordDescription", "passwordValidationFailedMessage"];
      for (const field of trimFields) {
        this[field] = this[field].trim();
      }
    }

    async submitForm() {
      // check regexp
      try {
        new RegExp(this.passwordPattern);
      } catch (e) {
        this.formErrorMsg = this.$t("invalidPasswordPattern").toString();
        return;
      }

      try {
        this.isSubmitting = true;

        const body = {
          minPasswordLength: +this.minPasswordLength,
          maxPasswordLength: +this.maxPasswordLength,
          passwordPattern: this.passwordPattern,
          passwordDescription: this.passwordDescription,
          passwordValidationFailedMessage: this.passwordValidationFailedMessage,
          passwordValidityPeriod: +this.passwordValidityPeriod,
          wrongPasswordAttempt: +this.wrongPasswordAttempt,
          lockedOut: this.lockedOut,
          lockedOutDuration: +this.lockedOutDuration,
          mustChangeDefaultPassword: this.mustChangeDefaultPassword
        };
        await passwordPolicyService.set(body);
        this.toastSuccessDelay(this.$t("saveSettingsSuccessful").toString(), null, 0);
        this.fetchPasswordPolicy();

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
